"use client";

import { createContext, useContext, useEffect, useRef, useState } from "react";
import { User } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase";
import { useRouter } from "next/navigation";
import { useNotificationStore } from "@/store/notification-store";
import { useGenerationStore } from "@/store/generation-store";
import { api } from "@/lib/api";

function clearPersistedUserState() {
    try {
        useGenerationStore.persist?.clearStorage();
    } catch {}
    try {
        useNotificationStore.persist?.clearStorage();
    } catch {}
    if (typeof window !== "undefined") {
        // Purge assistant chat histories (per-user keys all start with the prefix)
        try {
            for (let i = window.localStorage.length - 1; i >= 0; i--) {
                const key = window.localStorage.key(i);
                if (key && key.startsWith("ai-studio-assistant-chat")) {
                    window.localStorage.removeItem(key);
                }
            }
        } catch {}
    }
}

type AuthContextType = {
    user: User | null;
    isLoading: boolean;
    signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType>({ user: null, isLoading: true, signOut: async () => { } });

export const useAuth = () => useContext(AuthContext);

const LOADING_TIMEOUT_MS = 5000;

export function AuthProvider({ children }: { children: React.ReactNode }) {
    const [user, setUser] = useState<User | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const router = useRouter();
    const routerRef = useRef(router);
    routerRef.current = router;
    const syncNotifications = useNotificationStore((state) => state.syncWithBackend);

    useEffect(() => {
        if (user) {
            syncNotifications();
        }
    }, [user, syncNotifications]);

    // Safety-net poller for effect jobs. The TemplatesPanel runs its own faster
    // local poll, but unmounts when the user navigates away (e.g., to /gallery)
    // — losing the completion notification. This poller lives at the top of the
    // tree, so completion notifications fire even after the user navigates off.
    useEffect(() => {
        if (!user) return;
        let cancelled = false;
        const tick = async () => {
            const genState = useGenerationStore.getState();
            const pendings = genState.pendingGenerations.filter(
                (p) =>
                    p.status === "PENDING" &&
                    p.params?.mode === "effects" &&
                    typeof p.meta?.template_job_id === "string" &&
                    p.meta.template_job_id.length > 0
            );
            if (pendings.length === 0) return;
            for (const pg of pendings) {
                if (cancelled) return;
                const jobId = pg.meta?.template_job_id as string;
                try {
                    const res = await api.get(`/templates/jobs/${jobId}`);
                    const data = res.data as { status?: string; result?: string[]; error?: string };
                    // Re-read latest state in case local poller already settled this id.
                    const latest = useGenerationStore.getState().pendingGenerations.find((p) => p.id === pg.id);
                    if (!latest || latest.status !== "PENDING") continue;
                    const notifyStore = useNotificationStore.getState();
                    if (data.status === "completed") {
                        genState.updatePendingGeneration(pg.id, {
                            status: "COMPLETED",
                            result_url: data.result?.[0],
                        });
                        notifyStore.addNotification({
                            type: "success",
                            title: "Эффект готов",
                            message: "Откройте галерею, чтобы посмотреть результат.",
                            link: "/gallery",
                            metadata: { generationId: pg.id, prompt: pg.prompt },
                        });
                    } else if (data.status === "failed") {
                        genState.updatePendingGeneration(pg.id, { status: "FAILED" });
                        notifyStore.addNotification({
                            type: "error",
                            title: "Ошибка генерации эффекта",
                            message: data.error || "Не удалось завершить генерацию",
                            metadata: { generationId: pg.id, prompt: pg.prompt },
                        });
                    }
                } catch (e) {
                    console.error("Effect job safety-net poll error", e);
                }
            }
        };
        const interval = setInterval(tick, 8000);
        // Run once shortly after mount to catch jobs left over from a refresh.
        const initialTimer = setTimeout(tick, 3000);
        return () => {
            cancelled = true;
            clearInterval(interval);
            clearTimeout(initialTimer);
        };
    }, [user]);

    useEffect(() => {
        let mounted = true;

        const finishLoading = () => {
            if (mounted) setIsLoading(false);
        };

        // Hard timeout so isLoading never gets stuck if Supabase hangs
        const timeoutId = setTimeout(finishLoading, LOADING_TIMEOUT_MS);

        supabase.auth.getSession()
            .then(({ data: { session } }) => {
                if (!mounted) return;
                setUser(session?.user ?? null);
            })
            .catch((err) => {
                console.error("Session check failed", err);
            })
            .finally(() => {
                clearTimeout(timeoutId);
                finishLoading();
            });

        const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
            if (!mounted) return;
            setUser(session?.user ?? null);
            setIsLoading(false);
            if (event === 'SIGNED_OUT') {
                clearPersistedUserState();
                routerRef.current.push('/');
                routerRef.current.refresh();
            }
        });

        return () => {
            mounted = false;
            clearTimeout(timeoutId);
            subscription.unsubscribe();
        };
    }, []);

    const signOut = async () => {
        await supabase.auth.signOut();
        router.push('/');
    };

    return (
        <AuthContext.Provider value={{ user, isLoading, signOut }}>
            {children}
        </AuthContext.Provider>
    );
}
